import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import { api } from '../../utils/api';
import AdminLayout from './AdminLayout';
import JsonEditor from './JsonEditor';
import POIPreview from './POIPreview';

export default function AdminPOIs() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPOI, setEditingPOI] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'metro',
    subcategory: '',
    description: '',
    lat: '',
    lng: '',
    address: '',
    metadata: {}
  });

  const categories = [
    { value: 'metro', label: 'Μετρό', icon: 'Ⓜ️' },
    { value: 'rail', label: 'Προαστιακός', icon: '🚂' },
    { value: 'tram', label: 'Τραμ', icon: '🚊' },
    { value: 'ev_charger', label: 'Φορτιστής EV', icon: '⚡' },
    { value: 'parking', label: 'Πάρκινγκ', icon: '🅿️' },
    { value: 'bike_station', label: 'Σταθμός Ποδηλάτων', icon: '🚲' }
  ];

  useEffect(() => {
    loadPOIs();
  }, []);

  const loadPOIs = async () => {
    try {
      setLoading(true);
      const response = await api.getPOIs({ limit: 100 });
      setPois(response.pois || []);
    } catch (error) {
      console.error('Error loading POIs:', error);
      alert('Σφάλμα φόρτωσης POIs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.lat || !formData.lng) {
      alert('Παρακαλώ συμπληρώστε όλα τα απαιτούμενα πεδία');
      return;
    }

    try {
      const data = {
        ...formData,
        lat: parseFloat(formData.lat),
        lng: parseFloat(formData.lng),
        metadata: typeof formData.metadata === 'string' 
          ? JSON.parse(formData.metadata) 
          : formData.metadata
      };

      if (editingPOI) {
        // Update existing POI
        await api.adminUpdatePOI(token, editingPOI.id, data);
        alert('Το POI ενημερώθηκε επιτυχώς!');
      } else {
        // Create new POI
        await api.adminCreatePOI(token, data);
        alert('Το POI δημιουργήθηκε επιτυχώς!');
      }
      resetForm();
      loadPOIs();
    } catch (error) {
      console.error('Error saving POI:', error);
      alert('Σφάλμα αποθήκευσης POI: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'metro',
      subcategory: '',
      description: '',
      lat: '',
      lng: '',
      address: '',
      metadata: {}
    });
    setEditingPOI(null);
    setShowForm(false);
  };

  const handleEdit = (poi) => {
    setFormData({
      name: poi.name,
      category: poi.category,
      subcategory: poi.subcategory || '',
      description: poi.description || '',
      lat: poi.lat.toString(),
      lng: poi.lng.toString(),
      address: poi.address || '',
      metadata: JSON.stringify(poi.metadata || {}, null, 2)
    });
    setEditingPOI(poi);
    setShowForm(true);
  };

  const handleDelete = async (poi) => {
    if (!confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε το POI "${poi.name}";`)) {
      return;
    }

    try {
      await api.adminDeletePOI(token, poi.id);
      alert('Το POI διαγράφηκε επιτυχώς!');
      loadPOIs();
    } catch (error) {
      console.error('Error deleting POI:', error);
      alert('Σφάλμα διαγραφής POI: ' + error.message);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Διαχείριση POIs</h1>
          <p className="text-gray-600 mt-2">Προσθήκη και επεξεργασία σημείων ενδιαφέροντος</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/admin/pois/import')}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
          >
            📤 Bulk Import
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            {showForm ? '✕ Κλείσιμο' : '+ Νέο POI'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-6">
            {editingPOI ? 'Επεξεργασία POI' : 'Νέο POI'}
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Form Column */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Όνομα *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Κατηγορία *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Υποκατηγορία
                    </label>
                    <input
                      type="text"
                      value={formData.subcategory}
                      onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                      placeholder="π.χ. M1, M2, T1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Διεύθυνση
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Γεωγραφικό Πλάτος (Latitude) *
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={formData.lat}
                      onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                      placeholder="37.9838"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Γεωγραφικό Μήκος (Longitude) *
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={formData.lng}
                      onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                      placeholder="23.7275"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Περιγραφή
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <JsonEditor
                  value={formData.metadata}
                  onChange={(value) => setFormData({ ...formData, metadata: value })}
                  placeholder='{"line": "M1", "operator": "ΣΤΑΣΥ"}'
                />

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
                  >
                    {editingPOI ? 'Ενημέρωση' : 'Δημιουργία'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition"
                  >
                    Ακύρωση
                  </button>
                </div>
              </form>
            </div>

            {/* Preview Column */}
            <div>
              <div className="sticky top-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">👁️ Προεπισκόπηση</h3>
                <POIPreview poi={formData} metadata={formData.metadata} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">Λίστα POIs ({pois.length})</h2>
        </div>
        
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            Φόρτωση...
          </div>
        ) : pois.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            Δεν υπάρχουν POIs
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Όνομα</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Κατηγορία</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Τοποθεσία</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Κατάσταση</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ενέργειες</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pois.map((poi) => (
                  <tr key={poi.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{poi.name}</div>
                      {poi.subcategory && (
                        <div className="text-sm text-gray-500">{poi.subcategory}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {categories.find(c => c.value === poi.category)?.icon} {poi.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {poi.lat.toFixed(4)}, {poi.lng.toFixed(4)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {poi.is_active ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          Ενεργό
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                          Ανενεργό
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(poi)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Επεξεργασία
                      </button>
                      <button
                        onClick={() => handleDelete(poi)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Διαγραφή
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

